import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mantine/core';
import BestPriceLink from '@/components/common/BestPriceLink';
import { bestPriceTone } from '@/lib/bestOffer';
import { makeOfferUrl } from '@/lib/buyLinks';
import { formatMoney, formatProperty } from '@/lib/format';
import type { BestPriceByOrder, PrepaymentKey, PurchaseGoodsItem, PurchasePriceList, QualityRow } from '@/types/buy';
import classes from './PriceMatrix.module.css';

const PREPAYMENTS: readonly PrepaymentKey[] = ['0', '100'];

/** Состояние рынка: загружается, не загрузился (сообщение над таблицей у вызывающего) или готов. */
export type MarketState = 'loading' | 'failed' | 'ready';

interface PriceMatrixProps {
  readonly list: PurchasePriceList;
  readonly bestPrices: BestPriceByOrder;
  readonly market: MarketState;
}

const myPrice = (row: QualityRow, key: PrepaymentKey): number | null => row.pricesByPrepaymentMap?.[key] ?? null;

/** Выгода встречной цены для покупателя: моя цена минус цена продавца (больше нуля — дешевле моей). */
export function buyAdvantage(row: QualityRow, key: PrepaymentKey, bestPrices: BestPriceByOrder): number | undefined {
  const entry = row.orderId != null ? bestPrices.get(row.orderId)?.[key] : undefined;
  const mine = myPrice(row, key);
  return entry && mine != null ? mine - entry.price : undefined;
}

// Цены прайс-листа матрицей («Бюллетень»): строка — качество, по две колонки на каждую предоплату — моя цена и лучшая
// цена продавца с выгодой и кнопкой «Купить» (залита только у выгодной). Классы культуры — подзаголовками внутри одной
// таблицы, поэтому колонки всех классов совпадают. Где моей цены нет — «—» и без рынка. На телефоне строка — блок
// с подписанными парами.
export default function PriceMatrix({ list, bestPrices, market }: PriceMatrixProps) {
  const { t } = useTranslation();
  const prepaymentLabel = (key: PrepaymentKey): string => t(key === '0' ? 'app.buy.prepayment0Short' : 'app.buy.prepayment100Short');
  // Подзаголовок класса нужен, только если класс уточняет культуру или классов несколько.
  const withClassTitles = list.goodsList.length > 1 || list.goodsList.some((item) => item.goodsType !== item.parentGoodsType);

  const bestCell = (item: PurchaseGoodsItem, row: QualityRow, key: PrepaymentKey, quality: string) => {
    const label = t('app.buy.matrix.bestFor', { prepayment: prepaymentLabel(key) });
    const mine = myPrice(row, key);
    // Строка без моей цены или без заявки (шаблонная) рынка не имеет; сбой рынка объявлен над таблицей.
    if (mine == null || row.orderId == null || market === 'failed') {
      return <td className={classes.blank} data-label={label} />;
    }
    if (market === 'loading') {
      return (
        <td className={classes.best} data-label={label}>
          <Skeleton h={20} w={150} ml="auto" />
        </td>
      );
    }
    const entry = bestPrices.get(row.orderId)?.[key];
    if (!entry) {
      return (
        <td className={`${classes.best} ${classes.none}`} data-label={label}>
          {t('app.buy.noMarket')}
        </td>
      );
    }
    const advantage = mine - entry.price;
    return (
      <td className={classes.best} data-label={label}>
        <BestPriceLink
          to={makeOfferUrl({
            entry,
            fromPurchaseOrderId: row.orderId,
            toAddress: list.address,
            toAddressTitle: list.addressTitle,
            goods: item.parentGoodsType,
            advantage,
            myPrice: mine,
          })}
          price={entry.price}
          action="buy"
          tone={bestPriceTone(advantage)}
          advantage={advantage}
          context={[item.goodsType, quality, prepaymentLabel(key)].filter(Boolean).join(', ')}
        />
      </td>
    );
  };

  return (
    <table className={classes.table}>
      <colgroup>
        <col className={classes.colQuality} />
        <col className={classes.colMine} />
        <col className={classes.colBest} />
        <col className={classes.colMine} />
        <col className={classes.colBest} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" rowSpan={2}>
            {t('app.buy.quality')}
          </th>
          {PREPAYMENTS.map((key) => (
            <th key={key} scope="colgroup" colSpan={2} className={classes.group}>
              {prepaymentLabel(key)}
            </th>
          ))}
        </tr>
        <tr>
          {PREPAYMENTS.map((key) => (
            <Fragment key={key}>
              <th scope="col" className={classes.num}>
                {t('app.buy.matrix.mine')}
              </th>
              <th scope="col" className={classes.num}>
                {t('app.buy.matrix.best')}
              </th>
            </Fragment>
          ))}
        </tr>
      </thead>
      {list.goodsList.map((item) => (
        <tbody key={item.goodsType}>
          {withClassTitles ? (
            <tr className={classes.classRow}>
              <th scope="rowgroup" colSpan={5}>
                {item.goodsType}
              </th>
            </tr>
          ) : null}
          {item.qualityRows.map((row) => {
            const quality = row.properties.map((property) => formatProperty(property)).join(', ');
            return (
              <tr key={row.propsSetId}>
                <th scope="row" className={classes.quality}>
                  {quality || item.goodsType}
                </th>
                {PREPAYMENTS.map((key) => {
                  const mine = myPrice(row, key);
                  return (
                    <Fragment key={key}>
                      <td
                        className={`${classes.num} ${classes.mine}`}
                        data-label={t('app.buy.matrix.mineFor', { prepayment: prepaymentLabel(key) })}
                      >
                        {mine != null ? formatMoney(mine) : '—'}
                      </td>
                      {bestCell(item, row, key, quality)}
                    </Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      ))}
    </table>
  );
}
